import { BarChart3, Check, Lock, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

/** The hero's circular progress indicator (conic ring + centred %). */
export function ProgressRing({ percent }: { percent: number }) {
  return (
    <span
      className="relative flex size-[60px] shrink-0 items-center justify-center rounded-full"
      style={{
        background: `conic-gradient(var(--primary) 0 ${percent}%, var(--muted) ${percent}% 100%)`,
      }}
      aria-hidden
    >
      <span className="bg-card text-warning font-heading flex size-[46px] items-center justify-center rounded-full text-sm font-bold tabular-nums">
        {percent}%
      </span>
    </span>
  );
}

/** The navy "Add your first client" card at the foot of the step list. Clicking
 * it finishes setup and heads to the new-client flow. */
export function UnlockCard({ onUnlock }: { onUnlock: () => void }) {
  return (
    <button
      type="button"
      onClick={onUnlock}
      className="bg-brand-panel relative mt-2 flex items-center gap-3 overflow-hidden rounded-md p-3.5 text-left text-white"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute -top-10 -right-10 size-36 rounded-full opacity-40"
        style={{ background: "radial-gradient(closest-side, var(--primary), transparent)" }}
      />
      <span className="relative flex size-[38px] shrink-0 items-center justify-center rounded-sm bg-white/10">
        <Lock className="size-[17px]" />
      </span>
      <span className="relative flex flex-col leading-tight">
        <span className="text-[13px] font-semibold">Unlocks at the end: Add your first client</span>
        <span className="text-brand-panel-muted text-xs">
          Every client you add will land on a fully branded, ready workspace.
        </span>
      </span>
    </button>
  );
}

const ENCOURAGEMENTS = [
  {
    icon: Check,
    well: "bg-success-soft text-success",
    title: "Nothing is lost",
    body: <>Every step saves as you go — close the tab anytime.</>,
  },
  {
    icon: MessageSquare,
    well: "bg-info-soft text-info",
    title: "A human is nearby",
    body: (
      <>
        Stuck on a step?{" "}
        <Link to="/chat" className="text-info font-semibold">
          Message us
        </Link>{" "}
        — we reply in minutes.
      </>
    ),
  },
  {
    icon: BarChart3,
    well: "bg-warning-soft text-warning",
    title: "Firms that finish setup",
    body: <>add their first client the same day. You're minutes away.</>,
  },
] as const;

/** The reassurance strip under the hub body. */
export function EncouragementStrip() {
  return (
    <section className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
      {ENCOURAGEMENTS.map(({ icon: Icon, well, title, body }) => (
        <div
          key={title}
          className="bg-card/70 flex items-center gap-3 rounded-xl border p-4"
        >
          <span
            className={`flex size-9 shrink-0 items-center justify-center rounded-md ${well}`}
          >
            <Icon className="size-4" />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-[13px] font-semibold">{title}</span>
            <span className="text-muted-foreground text-xs">{body}</span>
          </span>
        </div>
      ))}
    </section>
  );
}
